// SETTINGS V2 — reusable "hardware/gesture Back closes the overlay" hook
// (SETTINGS-V2-SP1.1 / Q-SETV2-6 = B).
//
// Purpose: on phones, pressing the hardware/gesture Back while a transient
// overlay (bottom sheet, dialog) is open should CLOSE the overlay and stay on
// the current section — not navigate the router away. This hook makes that
// work for any v2 phone overlay driven by a boolean open flag, so future v2
// sheets/dialogs reuse it (the PM's "build it once as a platform pattern" note).
//
// How it works (and why it is safe with React Router v6 BrowserRouter):
//   • On open we push ONE history entry with the CURRENT url unchanged
//     (`pushState(state, "")` — no url arg keeps window.location as-is) tagged
//     with a private marker in history.state.
//   • pushState fires NO popstate, so React Router never sees the push.
//   • Because the sentinel has the SAME url, a later Back pops it and the
//     resulting popstate reads an UNCHANGED window.location — React Router keys
//     its matching off window.location, not history.state, so `<Routes>`
//     re-matches the identical pathname and does NOT remount the route tree or
//     re-run mount-time guards. The Back is consumed purely as "close overlay".
//   • On any NON-Back close we remove the sentinel ourselves, guarded so its
//     popstate is ignored — the user never needs a double-Back.
//
// Consumer contract (important):
//   If the overlay performs a NAVIGATION as one of its close actions (e.g. a
//   nav sheet whose rows go to a new route), that navigation MUST use REPLACE
//   (`<NavLink replace>` / `navigate(to, { replace: true })`). Replace consumes
//   the sentinel entry in place (turning it into the destination) so there is
//   no stray duplicate entry AND our cleanup correctly detects the marker is
//   gone and does not pop the freshly-pushed destination. Pure dialogs with no
//   internal navigation need no special handling — dismiss/Back/Escape all work
//   through this hook alone.
//
// SEC-REQ-2/3: no import-time side effects; no network/auth/localStorage/secrets
// — only window.history + a popstate listener at runtime while open.
import { useCallback, useEffect, useRef } from "react";

// Private marker stamped onto our sentinel's history.state so cleanup can tell
// whether our entry is still the current top (dismiss close) or has already
// been consumed by a replace-navigation (navigation close).
const SENTINEL_KEY = "__midHistoryBackClose";

/**
 * Wire hardware/gesture Back to close a transient overlay.
 * @param isOpen  whether the overlay is currently open.
 * @param onClose called when the user presses Back while open (idempotent).
 */
export default function useHistoryBackClose(
  isOpen: boolean,
  onClose: () => void
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // True while OUR sentinel entry is live on the history stack.
  const sentinelActiveRef = useRef(false);
  // Set right before our own history.back() so the popstate it triggers is
  // swallowed (that pop is our cleanup, not a user Back).
  const ignorePopRef = useRef(false);

  // Remove our sentinel if — and only if — it is still the current top entry.
  // A replace-navigation close will already have overwritten it (marker gone),
  // in which case popping would discard a real destination entry, so we skip.
  const removeSentinel = useCallback(() => {
    if (!sentinelActiveRef.current) return;
    sentinelActiveRef.current = false;
    const state = window.history.state as Record<string, unknown> | null;
    if (state && state[SENTINEL_KEY]) {
      ignorePopRef.current = true;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Push the same-url sentinel exactly once per open.
    sentinelActiveRef.current = true;
    window.history.pushState(
      { ...(window.history.state as object | null), [SENTINEL_KEY]: true },
      ""
    );

    const onPop = () => {
      if (ignorePopRef.current) {
        // Our own cleanup back() — swallow it, do nothing.
        ignorePopRef.current = false;
        return;
      }
      // Genuine user Back: the browser already popped our sentinel, so there
      // is nothing left to clean — just close the overlay in place.
      sentinelActiveRef.current = false;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);

    // Cleanup runs on close (isOpen -> false) AND on unmount-while-open (e.g.
    // resize out of phone unmounts the sheet). Both must strip the sentinel.
    return () => {
      window.removeEventListener("popstate", onPop);
      removeSentinel();
    };
  }, [isOpen, removeSentinel]);
}
